<script>
  import { callFunction } from "$lib/api";
  import {
    parseAction,
    colorForAction,
    cardToString,
    applySuitShift,
    applyFlopSwap,
    rowColToHandIndex,
    rowColToHandStr,
    handStrToHands,
    handStrWeightMultiplier,
  } from "$app/poker-helpers.js";
  import PlayerAction from "$app/PlayerAction.svelte";
  import { Hand } from "pokersolver";
  import CardSVG from "$app/CardSVG.svelte";
  import RangeTable from "$app/RangeTable.svelte";
  import RangeCategories from "$app/RangeCategories.svelte";
  import RangeList from "$app/RangeList.svelte";
  import XIcon from "@lucide/svelte/icons/x";

  let { result, preflopData, playerPositions, onclose, ontrack } = $props();

  ontrack ??= () => {}

  let selectedStep = $state(null);
  let loading = $state(false);

  // Determine if there was a showdown (no one folded before the terminal)
  let showdown = $derived.by(() => {
    for (const entry of result.sequence) {
      if (entry.action && entry.terminal === undefined) {
        const actionKind =
          typeof entry.action === "string"
            ? entry.action
            : Object.keys(entry.action)[0];
        if (actionKind === "fold") {
          return false;
        }
      }
    }
    // If no fold found and the sequence has a terminal entry, it was a showdown
    return result.sequence.some((e) => e.terminal);
  });

  // Compute hand descriptions for showdown
  let playerHandDescription = $derived.by(() => {
    if (!showdown) return null;
    const cards = result.boardCards
      .concat(result.playerCards)
      .map((c) => cardToString(applySuitShift(result.suitShift, c), true));
    return Hand.solve(cards).descr;
  });

  let opponentHandDescription = $derived.by(() => {
    if (!showdown) return null;
    const cards = result.boardCards
      .concat(result.opponentCards)
      .map((c) => cardToString(applySuitShift(result.suitShift, c), true));
    return Hand.solve(cards).descr;
  });

  // Loaded explore data
  let handTables = $state(null);
  let maxTableWeights = $state(null);
  let categoryTables = $state(null);
  let maxCategoryWeights = $state(null);
  let choices = $state(null);
  let decisionPlayer = $state(null);
  let range = $state(null);
  let strategy = $state(null);
  let selectedHands = $state(null);

  function isDecisionStep(entry) {
    return entry.action && entry.choices && entry.action.blind === undefined;
  }

  function findChoiceIndex(action, choices) {
    // indexOf won't work for object-type actions (e.g., {"raise": 4}) because
    // the snapshot creates different object references. Compare by value instead.
    if (typeof action === "string") {
      return choices.indexOf(action);
    }
    if (typeof action === "object" && action !== null) {
      const actionKey = Object.keys(action)[0];
      const actionVal = action[actionKey];
      for (let i = 0; i < choices.length; i++) {
        const choice = choices[i];
        if (typeof choice === "object" && choice !== null) {
          const choiceKey = Object.keys(choice)[0];
          if (choiceKey === actionKey && choice[choiceKey] === actionVal) {
            return i;
          }
        }
      }
    }
    return -1;
  }

  function findFlopIndex() {
    for (let i = 0; i < result.sequence.length; i++) {
      const entry = result.sequence[i];
      if (
        entry.chance &&
        Array.isArray(entry.chance) &&
        entry.chance.length === 3
      ) {
        return i;
      }
    }
    return -1;
  }

  function actionsEqual(a, b) {
    // Compare two action-sequence objects without depending on key order.
    if (a.player !== b.player) return false;
    if (typeof a.action !== typeof b.action) return false;
    if (typeof a.action === "string") return a.action === b.action;
    if (typeof a.action === "object" && a.action !== null) {
      const ak = Object.keys(a.action)[0];
      const bk = Object.keys(b.action)[0];
      return ak === bk && a.action[ak] === b.action[bk];
    }
    return false;
  }

  function actionSequencesMatch(seqA, seqB) {
    if (seqA.length !== seqB.length) return false;
    for (let i = 0; i < seqA.length; i++) {
      if (!actionsEqual(seqA[i], seqB[i])) return false;
    }
    return true;
  }

  function buildActionSequence(upToIndex) {
    // The preflop data's actionSequence contains actions that LED TO the decision
    // point, not including the decision itself. So we stop BEFORE upToIndex.
    const actions = [];
    for (let i = 0; i < upToIndex && i < result.sequence.length; i++) {
      const entry = result.sequence[i];
      if (entry.action != null && entry.player != null) {
        // Don't use shorthand — explicitly construct the object to own both keys.
        actions.push({ player: entry.player, action: entry.action });
      }
    }
    return actions;
  }

  function getPostflopActionIndices(flopIndex, upToIndex) {
    // Collect action indices that LED TO the clicked decision (not including it).
    const indices = [];
    for (let i = flopIndex; i < upToIndex && i < result.sequence.length; i++) {
      const entry = result.sequence[i];
      if (isDecisionStep(entry)) {
        const idx = findChoiceIndex(entry.action, entry.choices);
        if (idx >= 0) indices.push(idx);
      } else if (entry.chance && entry.index) {
        indices.push(entry.index);
      }
    }
    return indices;
  }

  function selectHandsCallback(h, c, cc) {
    const maxWeight = Math.max(...h.map((x) => x.weight), 0);
    selectedHands = {
      hands: h.toSorted((a, b) => b.weight - a.weight),
      maxWeight,
      choices: c,
      choiceColors: cc,
    };
  }

  function buildPreflopTables(dp) {
    const nPlayers = playerPositions.length;
    const newHandTables = [];
    const newMaxTableWeights = [];

    for (let player = 0; player < nPlayers; player++) {
      const table = new Array(169).fill(null).map(() => ({
        weight: 0,
        hands: [],
        strategy: null,
      }));
      let maxWeight = 0;

      for (let i = 0; i < 13; i++) {
        for (let j = 0; j < 13; j++) {
          const handStr = rowColToHandStr(i, j);
          const index = rowColToHandIndex(i, j);
          const weight =
            (dp.range[handStr] || 0) * handStrWeightMultiplier(handStr);
          const s =
            player === dp.player && dp.strategy
              ? dp.strategy[handStr] || null
              : null;
          if (weight > maxWeight) maxWeight = weight;

          const handStrHands = handStrToHands(handStr);
          const hands = handStrHands.map((hand) => ({
            hand,
            strategy: s,
            weight: weight / handStrHands.length,
          }));
          table[index] = { hands, weight, strategy: s };
        }
      }
      newHandTables[player] = table;
      newMaxTableWeights[player] = maxWeight;
    }

    handTables = newHandTables;
    maxTableWeights = newMaxTableWeights;
    range = dp.range;
    strategy = dp.strategy;
    choices = dp.actions;
    decisionPlayer = dp.player;
  }

  function buildPostflopTables(
    gameGraph,
    solution,
    localChoices,
    localDecisionPlayer,
    board,
    suitShift,
    rangeTables,
  ) {
    if (!solution || !gameGraph) {
      handTables = null;
      maxTableWeights = null;
      categoryTables = null;
      maxCategoryWeights = null;
      choices = localChoices;
      decisionPlayer = localDecisionPlayer;
      return;
    }

    // Use backend's pre-computed range tables (hand tables + category tables).
    // The backend already classifies hands and aggregates strategy/EV.
    if (rangeTables) {
      handTables = rangeTables.handTables;
      maxTableWeights = rangeTables.maxTableWeights;
      categoryTables = rangeTables.categoryTables;
      maxCategoryWeights = rangeTables.maxCategoryWeights;
    } else {
      handTables = null;
      maxTableWeights = null;
      categoryTables = null;
      maxCategoryWeights = null;
    }
    choices = localChoices;
    decisionPlayer = localDecisionPlayer;
  }

  async function handleStepClick(stepIndex) {
    if (!isDecisionStep(result.sequence[stepIndex])) return;
    if (loading) return;

    const entry = result.sequence[stepIndex];
    const { kind } = parseAction(entry.action);
    ontrack("hand_review_action", { action: kind });

    selectedStep = stepIndex;
    loading = true;
    selectedHands = null;

    try {
      const flopIdx = findFlopIndex();

      if (flopIdx < 0 || stepIndex < flopIdx) {
        // Preflop decision — use preflopData directly
        const actionSequence = buildActionSequence(stepIndex);
        const decisionPoints = preflopData.decisionPoints.filter((x) =>
          actionSequencesMatch(actionSequence, x.actionSequence),
        );

        if (decisionPoints.length === 1) {
          buildPreflopTables(decisionPoints[0]);
          categoryTables = null;
          maxCategoryWeights = null;
        } else {
          console.warn("No matching preflop decision point found");
        }
      } else {
        // Postflop decision — use stored solutionFile and solve_explore
        const solutionFilename = result.solutionFile;
        if (!solutionFilename) {
          console.error("No solutionFile stored for this result");
          return;
        }

        const postflopIndices = getPostflopActionIndices(flopIdx, stepIndex);

        const exploreResult = await callFunction("solve_explore", {
          filename: solutionFilename,
          actionIndices: postflopIndices,
        });

        if (exploreResult.Success) {
          const er = exploreResult.Success;
          const localChoices = er.currentNode?.node?.decision?.choices || null;
          const localDecisionPlayer =
            er.currentNode?.node?.decision?.player ?? null;
          const localBoard =
            er.currentNode?.node?.decision?.data?.board ||
            er.currentNode?.node?.chance?.data?.board ||
            result.boardCards;

          buildPostflopTables(
            er.gameGraph,
            er.solution,
            localChoices,
            localDecisionPlayer,
            localBoard,
            result.suitShift,
            er.rangeTables,
          );
          choices = localChoices;
          decisionPlayer = localDecisionPlayer;
          range = null;
          strategy = null;
        }
      }
    } catch (e) {
      console.error("Failed to load hand step:", e);
    } finally {
      loading = false;
    }
  }
</script>

<div class="flex flex-col space-y-4 w-full max-w-full min-h-0">
  <!-- Header with close button -->
  <div class="flex items-center justify-between">
    <span class="text-xs text-gray-400">Review hand</span>
    <button
      onclick={onclose}
      class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
      title="Back to results"
    >
      <XIcon size={16} />
    </button>
  </div>

  <!-- Action sequence -->
  <div class="flex flex-wrap gap-2">
    {#each result.sequence as entry, i}
      {#if entry.action && entry.chance === undefined && entry.terminal === undefined}
        {@const { kind, value } = parseAction(entry.action)}
        {@const color = colorForAction([entry.action], 0)}
        <button
          onclick={() => handleStepClick(i)}
          disabled={!isDecisionStep(entry) || loading}
          class="transition-opacity rounded"
          class:opacity-60={!isDecisionStep(entry)}
          class:cursor-pointer={isDecisionStep(entry)}
          class:cursor-default={!isDecisionStep(entry)}
          class:ring-2={selectedStep === i}
          class:ring-blue-400={selectedStep === i}
        >
          <PlayerAction
            playerName={playerPositions[entry.player]}
            labels={[kind, value]}
            {color}
            mistake={entry.mistake && !loading}
          />
        </button>
      {:else if entry.chance}
        <div class="w-full h-0"></div>
        <div class="flex gap-0">
          {#each (entry.chance.length === 3 && result.flopSwap ? applyFlopSwap(result.flopSwap, entry.chance) : entry.chance) as card}
            <div class="w-8 h-10">
              <CardSVG {card} suitShift={result.suitShift} />
            </div>
          {/each}
        </div>
        <div class="w-full h-0"></div>
      {:else if entry.terminal}{/if}
    {/each}
  </div>

  <!-- Board cards -->
  {#if result.boardCards && result.boardCards.length > 0}
    <div class="flex items-center gap-2">
      <span
        class="text-xs font-semibold"
        style="color: color-mix(in srgb, var(--poker-text) 50%, transparent)"
        >Board:</span
      >
      <div class="flex gap-0">
        {#each applyFlopSwap(result.flopSwap, result.boardCards) as card}
          <div class="w-8 h-10">
            <CardSVG {card} suitShift={result.suitShift} />
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Player / Opponent info and profit -->
  <div
    class="flex flex-col gap-3 p-3 rounded-lg text-sm"
    style="background-color: color-mix(in srgb, var(--poker-text) 8%, transparent);"
  >
    <div
      class="grid grid-cols-[auto_auto_auto_1fr] gap-x-2 gap-y-1 items-center"
      style="color: var(--poker-text);"
    >
      <!-- Player -->
      <span class="font-semibold"
        >{playerPositions[result.playerIndex ?? 0]}</span
      >
      <span
        style="color: color-mix(in srgb, var(--poker-text) 50%, transparent)"
        >(you)</span
      >
      <div class="flex gap-0">
        {#each result.playerCards as card}
          <div class="w-6 h-8">
            <CardSVG {card} suitShift={result.suitShift} border={false} />
          </div>
        {/each}
      </div>
      <span
        style="color: color-mix(in srgb, var(--poker-text) 65%, transparent)"
        >{playerHandDescription ?? ""}</span
      >

      <!-- Opponent -->
      <span class="font-semibold"
        >{playerPositions[result.opponentIndex ?? 1]}</span
      >
      <span></span>
      <div class="flex gap-0">
        {#each result.opponentCards as card}
          <div class="w-6 h-8">
            <CardSVG {card} suitShift={result.suitShift} border={false} />
          </div>
        {/each}
      </div>
      <span
        style="color: color-mix(in srgb, var(--poker-text) 65%, transparent)"
        >{opponentHandDescription ?? ""}</span
      >
    </div>

    <!-- Profit -->
    <div
      class="flex items-center gap-2 font-mono"
      style="color: var(--poker-text);"
    >
      <span
        style="color: color-mix(in srgb, var(--poker-text) 50%, transparent)"
        >Profit:</span
      >
      <span
        class={result.profits["player"] >= 0
          ? "text-green-500"
          : "text-red-500"}
      >
        {result.profits["player"] > 0 ? "+" : ""}{result.profits["player"]}
      </span>
    </div>

    <!-- Mistake indicator (only shown when present) -->
    {#if result.mistakeLoss != null}
      <div class="flex items-center gap-2" style="color: #f97316;">
        <span class="font-bold">Mistake</span>
      </div>
    {/if}
  </div>

  <!-- Loading indicator -->
  {#if loading}
    <div class="flex justify-center py-4">
      <span class="text-sm text-gray-400">Loading strategy…</span>
    </div>
  {/if}

  <!-- Strategy charts for the selected step -->
  {#if !loading && selectedStep !== null && handTables}
    <div class="flex flex-col space-y-4 w-full min-h-0 overflow-hidden h-fit">
      {#if choices && range && strategy}
        <!-- Preflop decision -->
        <div class="w-full min-h-0 overflow-hidden h-fit">
          <RangeTable
            {choices}
            {handTables}
            {maxTableWeights}
            player={decisionPlayer}
            selectHands={selectHandsCallback}
          />
        </div>
      {:else if choices && handTables && maxTableWeights && categoryTables && maxCategoryWeights}
        <!-- Postflop decision -->
        <div class="w-full min-h-0 overflow-hidden h-fit">
          <RangeTable
            {choices}
            {handTables}
            {maxTableWeights}
            player={decisionPlayer}
            selectHands={selectHandsCallback}
          />
        </div>
        <div class="w-full overflow-hidden h-fit">
          <RangeCategories
            {choices}
            {categoryTables}
            {maxCategoryWeights}
            player={decisionPlayer}
            selectHands={selectHandsCallback}
          />
        </div>
      {:else if handTables && maxTableWeights && !choices}
        <!-- Terminal / no decision — show both players' tables -->
        {#each playerPositions as _, i}
          <div class="w-full min-h-0 overflow-hidden h-fit">
            <RangeTable
              {handTables}
              {maxTableWeights}
              player={i}
              selectHands={selectHandsCallback}
            />
          </div>
          <div class="w-full overflow-hidden h-fit">
            <RangeCategories
              {categoryTables}
              {maxCategoryWeights}
              player={i}
              selectHands={selectHandsCallback}
            />
          </div>
        {/each}
      {/if}
    </div>

    <div class="overflow-hidden h-fit">
      <RangeList {selectedHands} suitShift={result.suitShift} />
    </div>
  {/if}
</div>
